// Reception date bar — RON-5469.
// Prototype region: `.date-bar` (`#date-prev`, `#date-display`, `#date-next`, `#date-today`).
//
// The date is the PAGE'S STATE, not a filter (§3): it combines with `filter` and `q` as one of
// three narrowing conditions on a single query, and it is what the whole board is "for".

import { useTranslation } from "react-i18next";

import { getRelativeDay } from "../receptionConstants.js";

// ISO in, ISO out, so nothing here ever constructs a Date from a string and inherits a timezone.
// `new Date("2026-04-28")` is UTC midnight, which is the previous day in any negative offset —
// the classic way a date bar goes one day out for half the world.
const shiftISODate = (iso, days) => {
  const [year, month, day] = String(iso).split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);

  return date.toISOString().slice(0, 10);
};

const formatLong = (iso) => {
  const [year, month, day] = String(iso).split("-").map(Number);

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-IE", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

// How many days the shown date sits from the server's today. Whole days, from the ISO strings, so
// no timezone enters into it — two dates a day apart are a day apart wherever the browser is.
const dayOffset = (dateISO, todayISODate) => {
  if (!dateISO || !todayISODate) return 0;

  const MS_PER_DAY = 86400000;

  return Math.round((Date.parse(`${dateISO}T00:00:00Z`) - Date.parse(`${todayISODate}T00:00:00Z`)) / MS_PER_DAY);
};

const ReceptionDateBar = ({ date, isToday, todayISO, onDateChange }) => {
  const { t } = useTranslation("common");

  if (!date) return null;

  const relativeDay = getRelativeDay(date, isToday, todayISO);
  const offset = dayOffset(date, todayISO);

  // The prototype names the near days and counts the far ones: YESTERDAY, TOMORROW, then
  // "3 DAYS AGO" / "IN 3 DAYS". A receptionist scanning back through a week reads a name faster
  // than a number, and a number is clearer than "LAST WEEK" once it is more than a day or two.
  const dayOffsetLabel = (() => {
    if (offset === -1) return t("react_reception_yesterday");
    if (offset === 1) return t("react_reception_tomorrow");
    if (offset < 0) return t("react_reception_days_ago_pill").replace("%s", String(-offset));

    return t("react_reception_in_days_pill").replace("%s", String(offset));
  })();

  return (
    <section className="reception-datebar">
      <span className="reception-datebar__eyebrow">{t("react_reception_showing_for")}</span>

      <button
        type="button"
        className="reception-datebar__arrow"
        onClick={() => onDateChange?.(shiftISODate(date, -1))}
        aria-label={t("react_reception_previous_day")}
        title={t("react_reception_previous_day")}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
      </button>

      {/* A native date input rather than a custom picker: it is the one control a receptionist
          already knows, it is keyboard-accessible for free, and the app's shared CustomDatePicker
          pulls in nine date-fns locales that this standalone tab has no other use for. */}
      <label className="reception-datebar__display">
        <span className="reception-datebar__text">{formatLong(date)}</span>
        {/* THE PILL IS ALWAYS THERE, with three states, as the prototype has it: TODAY in blue,
            YESTERDAY / N DAYS AGO in grey, TOMORROW / IN N DAYS in amber.

            We rendered it only on today, so every other day showed a bare date with nothing saying
            which way it sat from now — on a screen where reading yesterday's board as today's is
            the expensive mistake.

            `is_today` comes from the server, so this never compares clocks with it (§3); the
            past/future split uses the server's own today for the same reason. */}
        {isToday && <span className="reception-datebar__pill">{t("react_reception_today")}</span>}

        {/* NOT TODAY, AND WE KNOW WHICH WAY. The pill is withheld when `todayISO` is missing
            rather than falling back: `getRelativeDay` answers TODAY when it cannot tell, which is
            the right default for a stat tense and the wrong one here — it would print "TODAY" on
            a board that is not today, on the screen where reading yesterday's list as today's is
            the expensive mistake. No pill says less, and nothing false. */}
        {!isToday && todayISO && (
          <span
            className={`reception-datebar__pill reception-datebar__pill--${relativeDay}`}
          >
            {dayOffsetLabel}
          </span>
        )}
        <input
          type="date"
          className="reception-datebar__input"
          value={date}
          onChange={(event) => event.target.value && onDateChange?.(event.target.value)}
          aria-label={t("react_reception_pick_date")}
        />
      </label>

      <button
        type="button"
        className="reception-datebar__arrow"
        onClick={() => onDateChange?.(shiftISODate(date, 1))}
        aria-label={t("react_reception_next_day")}
        title={t("react_reception_next_day")}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
      </button>

      {/* Offered only when it would do something. A "jump to today" that is already today is a
          control that answers a question nobody asked. */}
      {!isToday && (
        <button
          type="button"
          /* NOT `--ghost`. The prototype's jump-to-today is a solid blue button; ours carried the
             ghost modifier and had no rule of its own, so it rendered as a white outline — the
             quietest control on the bar, for the one action that undoes being lost. */
          className="reception-btn reception-datebar__today"
          onClick={() => onDateChange?.(todayISO)}
        >
          {t("react_reception_jump_today")}
        </button>
      )}
    </section>
  );
};

export default ReceptionDateBar;
